import { useCallback, useState, type FormEvent } from "react";
import type { ApiError } from "../api/client";
import { useApi } from "../api/context";
import type { Tenant, TenantStatus } from "../api/types";
import { Field } from "../components/Field";
import { StatusBadge } from "../components/StatusBadge";
import { fieldMessage, toApiError } from "../lib/errors";
import { formatDate } from "../lib/format";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import { useLoader } from "../lib/useLoader";
import { useWorkspace } from "./workspace";

const TRANSITIONS: Record<TenantStatus, { status: TenantStatus; label: string }[]> = {
  onboarding: [{ status: "ready", label: "Mark ready" }],
  ready: [
    { status: "onboarding", label: "Back to onboarding" },
    { status: "suspended", label: "Suspend" },
  ],
  suspended: [{ status: "ready", label: "Reactivate" }],
};

export function TenantsPage() {
  useDocumentTitle("Workspaces · TryLook");
  const { isAdmin } = useWorkspace();
  if (!isAdmin) {
    return (
      <p className="alert alert-error" role="alert">
        Only TryLook administrators can manage workspaces.
      </p>
    );
  }
  return <TenantsAdmin />;
}

function TenantsAdmin() {
  const api = useApi();
  const loadTenants = useCallback(() => api.listTenants(), [api]);
  const tenants = useLoader(loadTenants);
  const [form, setForm] = useState({ tenantId: "", name: "", teamEmails: "", brief: "" });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busyTenantId, setBusyTenantId] = useState<string | null>(null);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setCreateError(null);
    setNotice(null);
    try {
      const { tenant } = await api.createTenant({
        tenant_id: form.tenantId.trim(),
        name: form.name.trim(),
        team_emails: form.teamEmails
          .split(/[\s,]+/)
          .map((email) => email.trim())
          .filter(Boolean),
        brief: form.brief.trim(),
      });
      setNotice(`${tenant.name} was created and is in onboarding.`);
      setForm({ tenantId: "", name: "", teamEmails: "", brief: "" });
      tenants.reload();
    } catch (error) {
      setCreateError(toApiError(error));
    } finally {
      setCreating(false);
    }
  }

  async function changeStatus(tenant: Tenant, status: TenantStatus) {
    setBusyTenantId(tenant.tenant_id);
    setNotice(null);
    try {
      await api.updateTenant(tenant.tenant_id, { status });
      setNotice(status === "ready" ? `${tenant.name} is now live.` : `${tenant.name} updated.`);
      tenants.reload();
    } catch (error) {
      setNotice(toApiError(error).message);
    } finally {
      setBusyTenantId(null);
    }
  }

  return (
    <>
      <div className="page-head">
        <h1>Workspaces</h1>
        <p>Onboard client workspaces and mark them ready once their product is mapped and the guide is tuned.</p>
      </div>
      {notice && (
        <p className="alert alert-info" role="status">
          {notice}
        </p>
      )}
      <section className="card">
        {tenants.error && (
          <p className="alert alert-error" role="alert">
            {tenants.error.message}
          </p>
        )}
        {tenants.data && (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th scope="col">Workspace</th>
                  <th scope="col">Status</th>
                  <th scope="col">Team notifications</th>
                  <th scope="col">Created</th>
                  <th scope="col">
                    <span className="visually-hidden">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenants.data.tenants.map((tenant) => (
                  <tr key={tenant.tenant_id}>
                    <td>
                      <div className="cell-primary">{tenant.name}</div>
                      <div className="muted">{tenant.tenant_id}</div>
                    </td>
                    <td>
                      <StatusBadge status={tenant.status} />
                    </td>
                    <td>{tenant.team_emails.join(", ") || <span className="muted">None</span>}</td>
                    <td>{formatDate(tenant.created_at)}</td>
                    <td>
                      <div className="actions">
                        {TRANSITIONS[tenant.status].map((transition) => (
                          <button
                            key={transition.status}
                            type="button"
                            className="btn btn-secondary btn-sm"
                            disabled={busyTenantId === tenant.tenant_id}
                            onClick={() => void changeStatus(tenant, transition.status)}
                            aria-label={`${transition.label}: ${tenant.name}`}
                          >
                            {transition.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <form className="card stack" onSubmit={(event) => void create(event)} noValidate>
        <h2 className="card-title">New workspace</h2>
        {createError && createError.details.length === 0 && (
          <p className="alert alert-error" role="alert">
            {createError.message}
          </p>
        )}
        <div className="grid-2">
          <Field
            label="Workspace ID"
            hint="Lowercase letters, numbers and dashes. Can't be changed later."
            error={fieldMessage(createError, "tenant_id")}
          >
            {(props) => (
              <input {...props} value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} />
            )}
          </Field>
          <Field label="Company name" error={fieldMessage(createError, "name")}>
            {(props) => <input {...props} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />}
          </Field>
        </div>
        <Field
          label="Team notification emails"
          hint="Comma-separated. These people get booking notifications and calendar invites."
          error={createError?.details.find((detail) => detail.field.startsWith("team_emails"))?.message}
        >
          {(props) => (
            <input {...props} value={form.teamEmails} onChange={(e) => setForm({ ...form, teamEmails: e.target.value })} />
          )}
        </Field>
        <Field
          label="Product brief"
          hint="What the guide may say about the product. It won't claim anything that isn't here."
          error={fieldMessage(createError, "brief")}
        >
          {(props) => (
            <textarea
              {...props}
              rows={6}
              maxLength={5000}
              value={form.brief}
              onChange={(e) => setForm({ ...form, brief: e.target.value })}
            />
          )}
        </Field>
        <div className="row">
          <button type="submit" className="btn btn-primary" disabled={creating}>
            {creating ? "Creating…" : "Create workspace"}
          </button>
        </div>
      </form>
    </>
  );
}
